import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Sparkles, Radio, Send, User, Users, Wifi, WifiOff, Zap, Clock, Database, Globe, Server, Repeat, Shield } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

interface Message {
    id: number;
    user: string;
    text: string;
    timestamp: Date;
    isOwn: boolean;
}

const SIMULATED_MESSAGES = [
    { user: 'Scout Alpha', text: 'Has anyone seen the latest deployment?' },
    { user: 'Scout Beta', text: 'Just pushed it to production! 🚀' },
    { user: 'Scout Gamma', text: 'The gorillas are going wild!' },
    { user: 'Scout Alpha', text: 'Real-time updates are magical ✨' },
];

export const RealtimeChat = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [simulationIndex, setSimulationIndex] = useState(0);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const connect = () => {
        setIsConnected(true);
        // Add initial "connected" message
        setMessages([{
            id: Date.now(),
            user: 'System',
            text: '🔌 Connected to Gorilla Radio Tower!',
            timestamp: new Date(),
            isOwn: false
        }]);
    };

    const disconnect = () => {
        setIsConnected(false);
        setMessages([]);
        setSimulationIndex(0);
    };

    // Simulate incoming messages
    useEffect(() => {
        if (!isConnected || simulationIndex >= SIMULATED_MESSAGES.length) return;

        const timer = setTimeout(() => {
            const simMsg = SIMULATED_MESSAGES[simulationIndex];
            setMessages(prev => [...prev, {
                id: Date.now(),
                user: simMsg.user,
                text: simMsg.text,
                timestamp: new Date(),
                isOwn: false
            }]);
            setSimulationIndex(i => i + 1);
        }, 2000 + Math.random() * 2000);

        return () => clearTimeout(timer);
    }, [isConnected, simulationIndex, messages]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = () => {
        if (!newMessage.trim() || !isConnected) return;

        setMessages(prev => [...prev, {
            id: Date.now(),
            user: 'You',
            text: newMessage,
            timestamp: new Date(),
            isOwn: true
        }]);
        setNewMessage('');

        if (!completed) {
            setCompleted(true);
            completeModule('realtime-subs', 100, 'badge_production');
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-blue-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="hidden md:flex bg-green-900 text-green-400 px-3 py-1 rounded-full text-xs font-bold items-center gap-1"><Sparkles size={12} /> Module Complete</div>}
                    <div className="bg-blue-900/50 text-blue-400 px-4 py-1 rounded-full text-sm font-bold border border-blue-800">
                        📡 Realtime Subscriptions
                    </div>
                    <div className="bg-slate-700/50 text-slate-400 px-3 py-1 rounded-full text-xs font-bold border border-slate-600 flex items-center gap-1">
                        <Shield size={12} /> Safe Simulation
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-white mb-2 flex items-center justify-center gap-3">
                        <Radio className="text-blue-500" /> Realtime Subscriptions
                    </h1>
                    <p className="text-slate-400 max-w-2xl mx-auto">
                        Experience real-time data sync. Connect to the "Radio Tower" and watch messages appear instantly—just like Supabase Realtime!
                    </p>

                    {/* Navigation to Exam */}
                    {completed && (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="mt-6"
                        >
                            <Link
                                to="/production-final-exam"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold py-3 px-8 rounded-2xl shadow-lg transition-all hover:scale-105"
                            >
                                <Sparkles size={20} />
                                Take Final Exam
                            </Link>
                        </motion.div>
                    )}
                </div>

                {/* Connection Status */}
                <div className="flex justify-center mb-8">
                    <button
                        onClick={isConnected ? disconnect : connect}
                        className={`px-8 py-4 rounded-2xl font-bold text-lg flex items-center gap-3 transition-all ${isConnected
                            ? 'bg-red-600 hover:bg-red-500 text-white'
                            : 'bg-green-600 hover:bg-green-500 text-white'
                            }`}
                    >
                        {isConnected ? (
                            <>
                                <WifiOff size={24} /> Disconnect
                            </>
                        ) : (
                            <>
                                <Wifi size={24} /> Connect to Radio Tower
                            </>
                        )}
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Chat Window 1 */}
                    <div className="bg-slate-800 rounded-3xl overflow-hidden border border-slate-700">
                        <div className="bg-slate-900 px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-slate-600'}`} />
                            <span className="font-bold">Scout HQ Channel</span>
                            <Users size={16} className="text-slate-500 ml-auto" />
                            <span className="text-sm text-slate-500">4 online</span>
                        </div>

                        <div className="h-80 p-4 overflow-y-auto space-y-3">
                            {!isConnected && (
                                <div className="text-center text-slate-600 py-12">
                                    <Radio size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>Click "Connect" to join the channel</p>
                                </div>
                            )}

                            <AnimatePresence>
                                {messages.map(msg => (
                                    <motion.div
                                        key={msg.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${msg.user === 'System'
                                            ? 'bg-slate-700 text-slate-400 text-center w-full'
                                            : msg.isOwn
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-slate-700 text-slate-200'
                                            }`}>
                                            {msg.user !== 'System' && !msg.isOwn && (
                                                <div className="text-xs text-blue-400 font-bold mb-1">{msg.user}</div>
                                            )}
                                            <div className="text-sm">{msg.text}</div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="p-4 border-t border-slate-700">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={e => setNewMessage(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && sendMessage()}
                                    disabled={!isConnected}
                                    placeholder={isConnected ? "Type a message..." : "Connect first..."}
                                    className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none disabled:opacity-50"
                                />
                                <button
                                    onClick={sendMessage}
                                    disabled={!isConnected || !newMessage.trim()}
                                    className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white px-4 rounded-xl transition-colors"
                                >
                                    <Send size={20} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Code Panel */}
                    <div className="bg-slate-800 rounded-3xl overflow-hidden border border-slate-700">
                        <div className="bg-slate-900 px-6 py-4 border-b border-slate-700">
                            <span className="font-bold text-slate-400">How It Works</span>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm">
                                <div className="text-slate-500 mb-2">// Supabase Realtime</div>
                                <div className="text-green-400">const channel = supabase</div>
                                <div className="text-green-400 pl-2">.channel('scout-hq')</div>
                                <div className="text-green-400 pl-2">.on('broadcast', {'{'} event: 'message' {'}'}, </div>
                                <div className="text-yellow-400 pl-4">(payload) =&gt; console.log(payload)</div>
                                <div className="text-green-400 pl-2">)</div>
                                <div className="text-green-400 pl-2">.subscribe();</div>
                            </div>

                            <div className="space-y-3 text-sm text-slate-400">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold">1</div>
                                    <div><strong className="text-white">Subscribe</strong> to a channel (like tuning a radio)</div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold">2</div>
                                    <div><strong className="text-white">Listen</strong> for events (new messages)</div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold">3</div>
                                    <div><strong className="text-white">Broadcast</strong> your own messages to others</div>
                                </div>
                            </div>

                            <div className="bg-blue-900/30 border border-blue-800 rounded-xl p-4 text-sm">
                                <strong className="text-blue-400">💡 Use Cases:</strong>
                                <ul className="text-slate-400 mt-2 space-y-1">
                                    <li>• Live chat applications</li>
                                    <li>• Collaborative editing (like Google Docs)</li>
                                    <li>• Live dashboards & notifications</li>
                                    <li>• Multiplayer games</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Educational Content Section */}
            <div className="max-w-4xl mx-auto px-8 pb-12 space-y-8 border-t border-slate-800 pt-12">

                {/* Theory Section */}
                <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700">
                    <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
                        📚 Understanding Real-Time
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg text-blue-400 mb-3">Polling vs. Pushing</h3>
                            <p className="text-slate-300 mb-4">
                                How does the browser know a new message arrived?
                            </p>

                            <div className="space-y-4">
                                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-600">
                                    <h4 className="font-bold text-slate-400 text-sm mb-1 flex items-center gap-2">
                                        <Repeat size={14} /> Long Polling (The Old Way)
                                    </h4>
                                    <p className="text-slate-500 text-xs">
                                        "Are we there yet? No. Are we there yet? No."
                                        <br />
                                        The client asks the server for updates every few seconds. Slow and wasteful.
                                    </p>
                                </div>
                                <div className="bg-blue-900/20 p-4 rounded-xl border border-blue-500/30">
                                    <h4 className="font-bold text-blue-400 text-sm mb-1 flex items-center gap-2">
                                        <Zap size={14} /> WebSockets (The New Way)
                                    </h4>
                                    <p className="text-blue-300 text-xs">
                                        "I'll tell you when we arrive."
                                        <br />
                                        A permanent 2-way connection. The server <strong>pushes</strong> data instantly.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-600">
                            <h3 className="font-bold text-lg text-white mb-3">📡 Channels & Rooms</h3>
                            <p className="text-slate-400 text-sm mb-4">
                                Imagine a hotel. A <strong>Socket</strong> is the front door.
                                <strong>Channels</strong> are the specific rooms.
                            </p>

                            <div className="space-y-3">
                                <div className="flex items-center gap-3 bg-slate-800 p-2 rounded-lg">
                                    <div className="w-8 h-8 bg-green-500/20 text-green-400 rounded flex items-center justify-center">
                                        <Users size={16} />
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-bold text-slate-300">Public Channel:</span>
                                        <span className="text-slate-500 ml-2">Broadcast to everyone (e.g. "User joined")</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-slate-800 p-2 rounded-lg">
                                    <div className="w-8 h-8 bg-purple-500/20 text-purple-400 rounded flex items-center justify-center">
                                        <User size={16} />
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-bold text-slate-300">Private Channel:</span>
                                        <span className="text-slate-500 ml-2">Direct Message to one user</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-slate-800 p-2 rounded-lg">
                                    <div className="w-8 h-8 bg-yellow-500/20 text-yellow-400 rounded flex items-center justify-center">
                                        <Database size={16} />
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-bold text-slate-300">Postgres Changes:</span>
                                        <span className="text-slate-500 ml-2">Listen to DB INSERT/UPDATE events</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tech Stack */}
                <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 p-8 rounded-3xl border border-blue-500/20">
                    <h2 className="text-xl font-bold text-white mb-4">🛠️ Real-Time Technologies</h2>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                            <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                                <Globe size={16} className="text-green-400" /> Socket.io
                            </h3>
                            <p className="text-slate-400 text-xs">
                                The most popular JavaScript library for WebSockets. Handles fallbacks automatically.
                            </p>
                        </div>
                        <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                            <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                                <Database size={16} className="text-blue-400" /> Supabase Realtime
                            </h3>
                            <p className="text-slate-400 text-xs">
                                Listens to your Postgres database and pushes changes to the client instantly.
                            </p>
                        </div>
                        <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                            <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                                <Server size={16} className="text-purple-400" /> WebRTC
                            </h3>
                            <p className="text-slate-400 text-xs">
                                Peer-to-Peer connection. Used for video calls and audio (Zoom/Google Meet).
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
